import React, { InputHTMLAttributes, useId, useState } from "react";
import { AlertCircleIcon, EyeIcon, EyeOffIcon, BoxIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";
interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
  icon?: BoxIcon;
  multiline?: boolean;
  rows?: number;
  onValueChange?: (v: string) => void;
}
export function TextField({
  label,
  error,
  hint,
  icon: Icon,
  type = "text",
  className,
  multiline,
  rows = 4,
  onValueChange,
  ...rest
}: TextFieldProps) {
  const id = useId();
  const [show, setShow] = useState(false);
  const isPassword = type === "password";
  const describedBy = error ? `${id}-err` : hint ? `${id}-hint` : undefined;
  const shell = twMerge("flex items-start gap-2.5 rounded-xl border-3 bg-surface px-3.5 transition-[transform,box-shadow] duration-150 ease-out focus-within:-translate-x-0.5 focus-within:-translate-y-0.5 focus-within:shadow-brutal-sm", error ? "border-bad" : "border-line");
  const input = "w-full min-w-0 bg-transparent py-3 text-[15px] text-ink placeholder:text-ink-2/70 outline-none";
  return <div className={className} data-id="element-1224" data-mp="1gmncw0pw5xjl">
      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-ink" data-id="element-1225" data-mp="12laxbqen7xy8">
        {label}
      </label>
      <div className={shell} data-id="element-1226" data-mp="16mmow1h2ut92">
        {Icon && <Icon className="mt-3.5 h-4 w-4 shrink-0 text-ink-2" strokeWidth={2.5} aria-hidden="true" data-id="element-1227" data-mp="16no48i54m1wl" />}
        {multiline ? <textarea id={id} rows={rows} aria-invalid={Boolean(error)} aria-describedby={describedBy} className={`${input} resize-none`} value={rest.value as string} placeholder={rest.placeholder} onChange={e => onValueChange?.(e.target.value)} data-id="element-1228" data-mp="1ud8yvyan6nlp" /> : <input id={id} type={isPassword && show ? "text" : type} aria-invalid={Boolean(error)} aria-describedby={describedBy} className={input} onChange={e => onValueChange?.(e.target.value)} {...rest} data-id="element-1229" data-mp="1plqu0z5i02vg" />}
        {isPassword && <button type="button" onClick={() => setShow(s => !s)} aria-label={show ? "Hide password" : "Show password"} className="focus-ring mt-2 rounded-lg p-1.5 text-ink-2 hover:text-ink" data-id="element-1230" data-mp="2i2bdsydw62ci">
            {show ? <EyeOffIcon className="h-4 w-4" data-id="element-1231" data-mp="2nw6syoduapy6" /> : <EyeIcon className="h-4 w-4" data-id="element-1232" data-mp="2nwygrd173jdw" />}
          </button>}
      </div>
      {error ? <p id={`${id}-err`} className="mt-1.5 flex items-center gap-1.5 text-sm font-medium text-bad" data-id="element-1233" data-mp="2iw2awtxa9n5u">
          <AlertCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1234" data-mp="2dxhaoui173he" />
          {error}
        </p> : hint ? <p id={`${id}-hint`} className="mt-1.5 text-sm text-ink-2" data-id="element-1235" data-mp="290qv08a529wi">
          {hint}
        </p> : null}
    </div>;
}